/**
 * Decorator utility for custom move implementations
 * Wraps custom move logic with common setup and cleanup code
 */

/**
 * Wraps custom move logic with common setup and cleanup code
 * @param {Function} customMoveLogic - Function that implements the specific move logic
 * @returns {Function} A function that matches the setUpBattleActions signature
 */
export function withCommonMoveSetup(customMoveLogic) {
    return function (battleStateMachine, move, attacker, defender) {
        // Common setup (lines 1280-1291)
        battleStateMachine.setUpBattleActionsContext(move, attacker, defender);

        if (battleStateMachine.context.attackerFainted) return battleStateMachine.context.actions;

        if (battleStateMachine.preventAttackerMoveDueToFlinch()) return battleStateMachine.context.actions;

        battleStateMachine.removeAttackerStatus();

        if (battleStateMachine.preventAttackerMoveDueToStatus()) return battleStateMachine.context.actions;

        battleStateMachine.announceMoveUsage();
        battleStateMachine.reduceMovePP();

        if (battleStateMachine.preventAttackerMoveDueToProtect()) return battleStateMachine.context.actions;

        // Execute custom move logic
        customMoveLogic(battleStateMachine, move, attacker, defender);

        // Common cleanup (lines 1320-1323)
        battleStateMachine.completeMove();
        battleStateMachine.readMoveEventTexts();
        battleStateMachine.performMoveAfterEffects();
        return battleStateMachine.context.actions;
    };
}

/**
 * Wraps custom move logic for single-hit moves that deal damage
 * Includes full damage dealing logic, then calls custom logic before completion
 * @param {Function} customMoveLogic - Function that implements custom effects after damage is dealt
 * @returns {Function} A function that matches the setUpBattleActions signature
 */
export function withSingleHitMove(customMoveLogic) {
    return function (battleStateMachine, move, attacker, defender) {
        // Common setup (lines 1294-1307)
        battleStateMachine.setUpBattleActionsContext(move, attacker, defender);

        if (battleStateMachine.context.attackerFainted) return battleStateMachine.context.actions;

        if (battleStateMachine.preventAttackerMoveDueToFlinch()) return battleStateMachine.context.actions;

        battleStateMachine.removeAttackerStatus();

        if (battleStateMachine.preventAttackerMoveDueToStatus()) return battleStateMachine.context.actions;

        battleStateMachine.announceMoveUsage();
        battleStateMachine.reduceMovePP();

        if (battleStateMachine.preventAttackerMoveDueToProtect()) return battleStateMachine.context.actions;

        // Damage dealing logic (lines 1309-1337)
        battleStateMachine.calculateNumberOfHits();
        if (!battleStateMachine.shouldMoveHitTarget()) return battleStateMachine.context.actions;

        if (!battleStateMachine.shouldMoveAffect()) {
            // Add effectiveness text to actions array
            battleStateMachine.context.actions.push(
                battleStateMachine.ag.readText(
                    battleStateMachine.effectivenessBattleText(battleStateMachine.context.move, battleStateMachine.context.defender),
                    { delay: 50 }
                )
            );
            return battleStateMachine.context.actions;
        }

        battleStateMachine.calculateVolatileStatusEffectApplicationSuccess();
        if (battleStateMachine.shouldPreventAttackDueToFailure()) return battleStateMachine.context.actions;

        // Single hit damage dealing
        battleStateMachine.calculateDamageDealt();
        battleStateMachine.animateAttack();
        battleStateMachine.dealDamage();
        battleStateMachine.clearActionsIfDefenderFainted();
        battleStateMachine.applyStatModifiers();
        battleStateMachine.applyVolatileStatusEffect();
        battleStateMachine.applyNonVolatileStatusEffect();
        battleStateMachine.applyEntryHazards();
        battleStateMachine.recoverHP();
        battleStateMachine.dealRecoilDamage();
        battleStateMachine.clearActionsIfDefenderFainted();

        // Execute custom move logic (before completeMove)
        customMoveLogic(battleStateMachine, move, attacker, defender);

        // Common cleanup (lines 1338-1340)
        battleStateMachine.completeMove();
        battleStateMachine.readMoveEventTexts();
        battleStateMachine.performMoveAfterEffects();
        return battleStateMachine.context.actions;
    };
}
